import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
} from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { BASE_URL } from "../constants";

const OrgVerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialEmail = location.state?.orgEmail || location.state?.email || "";
  const initialOrgId = location.state?.orgId || "";
  const initialOrgName = location.state?.orgName || "";

  const [orgEmail, setOrgEmail] = useState(initialEmail);
  const [orgId] = useState(initialOrgId);
  const [orgName] = useState(initialOrgName);
  const [isEditingEmail, setIsEditingEmail] = useState(!initialEmail);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(
    location.state?.message ||
      (initialEmail
        ? `A 6-digit verification OTP has been sent to ${initialEmail}.`
        : ""),
  );
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef([]);

  // Countdown timer for Resend OTP
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Handle OTP digit change and auto-advance
  const handleOtpChange = (index, value) => {
    // Only accept numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];

    // If user typed/pasted multiple digits
    if (value.length > 1) {
      const digits = value.slice(0, 6).split("");
      for (let i = 0; i < 6; i++) {
        newOtp[i] = digits[i] || "";
      }
      setOtp(newOtp);
      const nextFocus = Math.min(digits.length, 5);
      inputRefs.current[nextFocus]?.focus();
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next box
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle Backspace navigation
  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle Paste event to fill all 6 boxes
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = digits[i] || "";
    }
    setOtp(newOtp);

    const nextIndex = Math.min(digits.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  // Verify OTP submission
  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSuccess("");

    const targetEmail = orgEmail.trim();
    if (!targetEmail) {
      setError("Please enter your organization email address.");
      return;
    }

    const enteredOtp = otp.join("");
    if (enteredOtp.length < 6) {
      setError("Please enter the complete 6-digit OTP.");
      return;
    }

    setLoading(true);
    const payload = {
      orgEmail: targetEmail,
      email: targetEmail,
      otp: enteredOtp,
      orgId: orgId || undefined,
    };

    try {
      let res;
      try {
        res = await axios.post(`${BASE_URL}api/org/verify-otp`, payload, {
          headers: { "Content-Type": "application/json" },
        });
      } catch (err) {
        // Fallback to alias route /verify-email-otp if 404
        if (err.response?.status === 404) {
          res = await axios.post(
            `${BASE_URL}api/org/verify-email-otp`,
            payload,
            {
              headers: { "Content-Type": "application/json" },
            },
          );
        } else {
          throw err;
        }
      }

      if (res.data?.success) {
        setSuccess(
          res.data.message ||
            "Organization email verified successfully! Redirecting to login...",
        );
        setTimeout(() => {
          navigate("/org-login", {
            state: { orgEmail: targetEmail },
          });
        }, 1800);
      } else {
        setError(
          res.data?.message || "Invalid or expired OTP. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError(
        err.response?.data?.message ||
          "Verification failed. Please check the OTP or request a new one.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    const targetEmail = orgEmail.trim();
    if (!targetEmail) {
      setError("Please enter your organization email address.");
      return;
    }

    setResending(true);
    setError("");
    setSuccess("");

    const payload = {
      orgEmail: targetEmail,
      email: targetEmail,
      orgId: orgId || undefined,
      orgName: orgName || undefined,
    };

    try {
      let res;
      try {
        res = await axios.post(`${BASE_URL}api/org/send-verify-otp`, payload, {
          headers: { "Content-Type": "application/json" },
        });
      } catch (err) {
        // Fallback to alias route /send-email-otp if 404
        if (err.response?.status === 404) {
          res = await axios.post(`${BASE_URL}api/org/send-email-otp`, payload, {
            headers: { "Content-Type": "application/json" },
          });
        } else {
          throw err;
        }
      }

      if (res.data?.success) {
        setSuccess(
          res.data.message ||
            `A new verification OTP has been sent to ${targetEmail}.`,
        );
        setOtp(["", "", "", "", "", ""]);
        setTimer(60);
        setCanResend(false);
        inputRefs.current[0]?.focus();
      } else {
        setError(
          res.data?.message || "Failed to resend OTP. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error resending OTP:", err);
      setError(
        err.response?.data?.message ||
          "Failed to resend OTP. Please wait before trying again.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#F1F5F9",
        p: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 20px 45px rgba(15, 23, 42, 0.1)",
          border: "1px solid #E2E8F0",
        }}
      >
        {/* Header Icon & Title */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "16px",
              backgroundColor: "#EFF6FF",
              color: "#2563EB",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
              boxShadow: "0 8px 20px rgba(37, 99, 235, 0.15)",
            }}
          >
            <MarkEmailReadIcon sx={{ fontSize: 34 }} />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#0F172A",
              fontSize: "1.45rem",
              mb: 0.5,
            }}
          >
            Verify Organization Email
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#64748B", fontSize: "0.88rem", lineHeight: 1.5 }}
          >
            Enter the 6-digit verification code sent to your registered
            organization email to activate your account.
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: "8px" }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 2.5, borderRadius: "8px" }}
          >
            {success}
          </Alert>
        )}

        {/* Email Address Display / Edit Box */}
        <Box
          sx={{
            mb: 3,
            p: 1.5,
            px: 2,
            backgroundColor: "#F8FAFC",
            borderRadius: "10px",
            border: "1px solid #E2E8F0",
          }}
        >
          {isEditingEmail ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Organization Email"
                type="email"
                value={orgEmail}
                onChange={(e) => setOrgEmail(e.target.value)}
                placeholder="Enter organization email"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsEditingEmail(false)}
                disabled={!orgEmail.trim()}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Set
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  Verifying Email For:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#0F172A",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {orgEmail || "No email specified"}
                </Typography>
                {orgName && (
                  <Typography variant="caption" sx={{ color: "#2563EB" }}>
                    Organization: {orgName}
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                onClick={() => setIsEditingEmail(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: "#2563EB",
                  minWidth: "auto",
                }}
              >
                Change
              </Button>
            </Box>
          )}
        </Box>

        {/* 6-Digit OTP Boxes */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 3,
          }}
          onPaste={handlePaste}
        >
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  padding: "10px 0",
                  height: "32px",
                },
              }}
              sx={{
                width: "15%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  borderColor: digit ? "#2563EB" : "#CBD5E1",
                  backgroundColor: digit ? "#F0F7FF" : "#FFFFFF",
                  boxShadow: digit
                    ? "0 0 0 3px rgba(37, 99, 235, 0.12)"
                    : "none",
                },
              }}
            />
          ))}
        </Box>

        {/* Expiry Note */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 3,
            color: "#64748B",
            fontSize: "0.82rem",
          }}
        >
          <span>⏱️ OTP is valid for 10 minutes</span>
        </Box>

        {/* Verify Button */}
        <Button
          variant="contained"
          fullWidth
          disabled={loading || otp.join("").length < 6}
          onClick={handleVerify}
          sx={{
            py: 1.4,
            fontSize: "0.95rem",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
            boxShadow: "0 6px 18px rgba(37, 99, 235, 0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
              boxShadow: "0 8px 22px rgba(37, 99, 235, 0.45)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
          ) : (
            "Verify & Activate Organization"
          )}
        </Button>

        {/* Resend OTP Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 3,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748B" }}>
            Didn't receive the email?
          </Typography>

          {canResend ? (
            <Button
              size="small"
              onClick={handleResendOtp}
              disabled={resending}
              startIcon={
                resending ? (
                  <CircularProgress size={14} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#2563EB",
                fontSize: "0.85rem",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  background: "none",
                  textDecoration: "underline",
                },
              }}
            >
              {resending ? "Sending..." : "Resend OTP"}
            </Button>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: "#94A3B8", fontWeight: 600 }}
            >
              Resend in {timer}s
            </Typography>
          )}
        </Box>

        {/* Navigation Links */}
        <Box
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            component={Link}
            to="/org-register"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              color: "#64748B",
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            Back to Register
          </Button>

          <Button
            component={Link}
            to="/org-login"
            sx={{
              textTransform: "none",
              color: "#2563EB",
              fontSize: "0.82rem",
              fontWeight: 700,
            }}
          >
            Go to Login &rarr;
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default OrgVerifyOtp;
